import { useEffect, useMemo, useState } from 'react';

import {
  buildFormAnswerPayload,
  EntityRefUtil,
  formatManuallyWithUserTimezone,
  FormDTO,
  FormElementKind,
  isSubmissionEditable,
  TableFormElement,
  type UpdateInspectionRequestDTO,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { FormProvider, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { RevisionHistory } from '@/components/revisions/revision-history';
import { SingleRecordPdfControls } from '@/components/single-record-pdf-controls';
import { TagBadges } from '@/components/table-page/columns/tag-badges';
import { ReadOnlyField } from '@/components/ui/read-only-field';
import { Sheet, SheetContent, SheetHeader, SheetPortal, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/toaster';
import { useSaveChangesGuard } from '@/hooks/use-save-changes-guard';
import { cn, initials } from '@/lib/utils';
import {
  activityKeys,
  getInspectionByIdQueryOptions,
  getInspectionFormQueryOptions,
  getInspectionRevisionsQueryOptions,
  inspectionKeys,
} from '@/queries/inspections';
import { ApiService } from '@/services/api-v2';
import { AWAApiV2Service } from '@/services/awa-api-v2';
import { useUserStore } from '@/stores/user.store';
import { useIsAWA } from '@/utils/awa';
import { LegacySubmissionRenderer } from '@/utils/legacy';
import { getWorkspaceRoute } from '@/utils/routes';

import { SaveChangesDialog } from '../common/save-changes-dialog';
import { initializeTableInitialValues } from '../form/elements/table';
import { FormElementsRenderer, FormRendererData } from '../form/form-elements-renderer';
import ActivityIcon from '../icons/activity';
import ArrowLeftIcon from '../icons/arrow-left';
import CloseIcon from '../icons/close';
import SendIcon from '../icons/send';
import WarningIcon from '../icons/warning';
import { ShareDrawer } from '../share/drawer';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Label } from '../ui/label';
import { Skeleton } from '../ui/skeleton';

export default function InspectionDrawer({
  open: openProp,
  onOpenChange,
  inspectionId,
  noTrigger = false,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  inspectionId?: string;
  noTrigger?: boolean;
}) {
  const userTimezone = useUserStore.getState().user?.timezone;
  const [open, setOpen] = useState(openProp ?? false);
  const { isAWA } = useIsAWA();
  const [isShareDrawerOpen, setIsShareDrawerOpen] = useState(false);
  const [view, setView] = useState<'form' | 'activity'>('form');

  useEffect(() => {
    setOpen(openProp ?? false);
  }, [openProp]);

  const handleOpenChange = (open: boolean) => {
    onOpenChange?.(open);
    setOpen(open);
    setIsShareDrawerOpen(false);
    if (!open) {
      setView('form');
    }
  };

  const { data: inspectionData, isFetching: isFetchingInspectionData } = useQuery({
    ...getInspectionByIdQueryOptions({ inspectionId, isAWA }),
    enabled: !!inspectionId && open,
  });
  const { data: inspectionFormData, isFetching: isFetchingInspectionFormData } = useQuery({
    ...getInspectionFormQueryOptions({ formRevisionId: inspectionData?.inspection.formRevisionId, isAWA }),
    enabled: !!inspectionId && !!inspectionData?.inspection.formRevisionId,
  });
  const inspectionForm = useMemo(() => inspectionFormData?.formRevision, [inspectionFormData]);

  const { data: revisions, isLoading: isLoadingRevisions } = useQuery({
    ...getInspectionRevisionsQueryOptions({ inspectionId, isAWA }),
    enabled: !!inspectionId && open && view === 'activity',
  });

  const formInstance = useForm<FormRendererData>({
    defaultValues: {
      ...inspectionForm?.elements.reduce(
        (acc, element) => ({
          ...acc,
          ...(element.kind === FormElementKind.Table
            ? {
                [element.id]: [[...initializeTableInitialValues(element as TableFormElement)]],
              }
            : {
                [element.id]: undefined,
              }),
        }),
        {} as FormRendererData,
      ),
    },
  });

  const isLegacySubmission = useMemo(() => inspectionData?.inspection.legacyData !== null, [inspectionData?.inspection.legacyData]);

  useEffect(() => {
    if (inspectionData?.inspection.data) {
      formInstance.reset(
        inspectionData.inspection.data
          .filter((element): element is NonNullable<typeof element> => element !== null)
          .reduce(
            // Type assertion until we migrate to V2 endpoint
            (acc, element) => ({ ...acc, [(element as unknown as { elementId: string }).elementId]: element.value }),
            {} as FormRendererData,
          ),
      );
    }
  }, [inspectionData?.inspection.data, formInstance]);

  const toast = useToast();
  const queryClient = useQueryClient();

  // Open editing: any logged-in user can edit an eligible record — no owner/role gate.
  const canEditRecord = !!inspectionData?.inspection && !!inspectionForm && isSubmissionEditable(inspectionData.inspection);
  const isFormDirty = formInstance.formState.isDirty;

  const {
    mutate: saveInspectionEdit,
    mutateAsync: saveInspectionEditAsync,
    isPending: isSaving,
  } = useMutation({
    mutationFn: async (values: FormRendererData) => {
      if (!inspectionId) {
        throw new Error('Missing inspection id');
      }
      const data = buildFormAnswerPayload(inspectionForm?.elements ?? [], values);
      const payload: UpdateInspectionRequestDTO = { data };
      return await (await (isAWA ? AWAApiV2Service.UpdateInspection(inspectionId, payload) : ApiService.UpdateInspection(inspectionId, payload))).json();
    },
    onError: () => {
      toast.error('Unable to save changes. Please try again.');
    },
    onSuccess: () => {
      toast.success('Inspection record updated');
      queryClient.invalidateQueries({ queryKey: inspectionKeys.all });
      if (inspectionData?.inspection.assetId) {
        queryClient.invalidateQueries({ queryKey: activityKeys.byAsset(inspectionData.inspection.assetId) });
      }
    },
  });

  const handleInvalidSave = () => {
    toast.error('The record has invalid or incomplete data. Please check.');
    const [firstErrorField] = Object.keys(formInstance.formState.errors);
    if (firstErrorField) {
      document.getElementById(firstErrorField)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const { isConfirmOpen, confirmSafeToLeave, settleConfirm, onConfirmOpenChange } = useSaveChangesGuard(() => formInstance.formState.isDirty);

  async function closeDrawer() {
    if (!(await confirmSafeToLeave())) return;
    handleOpenChange(false);
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(newOpen) => {
        if (newOpen) {
          handleOpenChange(true);
        } else {
          closeDrawer();
        }
      }}>
      {!noTrigger && (
        <SheetTrigger asChild>
          <Button className="h-full rounded-none p-2" variant="ghost">
            View Inspection
          </Button>
        </SheetTrigger>
      )}
      <SheetPortal>
        <SheetContent hideCloseIcon side={'right'} className={cn(isAWA ? 'mx-auto h-full w-full sm:max-w-[800px]' : '')}>
          <SheetHeader className="border-grey-3 h-[64px] flex-row items-center justify-between space-y-0 border-b bg-white">
            {isAWA ? (
              <>
                <div className="flex items-center gap-2 py-1">
                  <button className="-m-2 p-2" type="button" onClick={() => closeDrawer()}>
                    <ArrowLeftIcon className="size-6" />
                  </button>
                  <SheetTitle className="text-xl">{inspectionData?.inspection ? 'Inspection record' : 'Create Inspection'}</SheetTitle>
                </div>
                {canEditRecord && (
                  <Button
                    variant="ghost"
                    className="text-default-green text-lg font-medium"
                    loading={isSaving}
                    disabled={!isFormDirty || isSaving}
                    onClick={() => formInstance.handleSubmit((values) => saveInspectionEdit(values), handleInvalidSave)()}>
                    Save
                  </Button>
                )}
              </>
            ) : (
              <>
                <SheetTitle>{isFetchingInspectionData || isFetchingInspectionFormData ? 'Loading inspection details...' : 'Inspection record'}</SheetTitle>
                <div className="flex items-center gap-2">
                  {canEditRecord && (
                    <Button
                      variant="success"
                      loading={isSaving}
                      disabled={!isFormDirty || isSaving}
                      onClick={() => formInstance.handleSubmit((values) => saveInspectionEdit(values), handleInvalidSave)()}>
                      Save
                    </Button>
                  )}
                  {inspectionData?.inspection && !isLegacySubmission && (
                    <Button
                      variant="grey"
                      size="default"
                      type="button"
                      icon={<ActivityIcon />}
                      onClick={() => setView((prev) => (prev === 'activity' ? 'form' : 'activity'))}>
                      {view === 'activity' ? 'Hide Activity' : 'Show Activity'}
                    </Button>
                  )}
                  {inspectionData?.inspection && (
                    <SingleRecordPdfControls
                      recordType="inspection"
                      recordId={inspectionData.inspection.id}
                      documentUrl={inspectionData.inspection.document?.url}
                    />
                  )}
                  <button
                    type="button"
                    className="data-[state=open]:bg-default-blue cursor-pointer rounded-sm opacity-70 transition-opacity outline-none hover:opacity-100 disabled:pointer-events-none"
                    onClick={() => closeDrawer()}>
                    <CloseIcon className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                  </button>
                </div>
              </>
            )}
          </SheetHeader>
          {(isFetchingInspectionData && !inspectionData) || (isFetchingInspectionFormData && !inspectionFormData) ? (
            <div className="flex flex-col gap-2 px-4 pt-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex flex-col gap-1">
                  {[...Array(2)].map((_, i) => (
                    <Skeleton key={i} className={cn('h-4 w-32', i === 1 && 'w-28')} />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex flex-col items-end gap-1">
                    {[...Array(2)].map((_, i) => (
                      <Skeleton key={i} className={cn('h-4 w-28', i === 1 && 'w-32')} />
                    ))}
                  </div>
                  <Skeleton className="size-7 rounded-full" />
                </div>
              </div>

              <div className="border-grey-2 animate-pulse border-b-4"></div>

              <div className="flex flex-col gap-4 px-2 py-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full rounded px-6" />
                ))}

                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className={cn('h-12 w-52 rounded px-6', i === 1 && 'h-32 w-full', i !== 1 && 'self-center')} />
                ))}
              </div>
            </div>
          ) : (
            <div className="flex h-full flex-1 flex-col overflow-y-auto">
              {view === 'form' && !!inspectionData?.inspection && (
                <div className="flex flex-col gap-1 px-4 pt-4">
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <div className="flex flex-col gap-[2px]">
                      <div>
                        <span className="font-medium">ID: </span> {EntityRefUtil.createInspectionRef(inspectionData.inspection.number)}
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="font-medium">Issues: </span>
                        <div className="bg-default-light-orange flex items-center gap-1 rounded px-1.5 py-0.5 text-sm text-white">
                          <WarningIcon className="text-default-dark-orange" size={20} />
                          <span className="text-default-dark-orange">{inspectionData.inspection.issueCount || 0}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-right">
                      <div className="flex flex-col gap-[2px]">
                        <div className="line-clamp-1 font-medium">{EntityRefUtil.createUserFullNameRef(inspectionData.inspection.inspectedBy, '-')}</div>
                        <div className="text-secondary-text">
                          {isAWA
                            ? dayjs(inspectionData.inspection.inspectionDate).format('MMM DD, YYYY hh:mm a')
                            : formatManuallyWithUserTimezone(new Date(inspectionData.inspection.inspectionDate), 'MMM DD, YYYY hh:mm a', userTimezone)}
                        </div>
                      </div>
                      <Avatar>
                        {inspectionData.inspection.inspectedBy && inspectionData.inspection.inspectedBy.avatarUrl ? (
                          <AvatarImage src={inspectionData.inspection.inspectedBy.avatarUrl} />
                        ) : (
                          <AvatarFallback>{initials(EntityRefUtil.createUserFullNameRef(inspectionData.inspection.inspectedBy, '-'))}</AvatarFallback>
                        )}
                      </Avatar>
                    </div>
                  </div>

                  <div className="border-default-blue border-b-4" />
                </div>
              )}

              <div
                className={cn(
                  'scrollbar scrollbar-track-transparent scrollbar-thumb-grey-3 flex flex-1 flex-col gap-2 overflow-y-auto',
                  view === 'form' && 'px-6 pt-4 pb-6 print:px-0 print:pt-4 print:pb-4',
                )}>
                {view === 'activity' ? (
                  inspectionForm && inspectionData?.inspection.asset ? (
                    <RevisionHistory
                      revisions={revisions ?? []}
                      isLoading={isLoadingRevisions}
                      elements={inspectionForm.elements}
                      form={inspectionForm.form as FormDTO}
                      asset={inspectionData.inspection.asset}
                    />
                  ) : (
                    <div className="text-grey-5 p-4 text-sm">Loading…</div>
                  )
                ) : (
                  inspectionData?.inspection && (
                    <>
                      {!!inspectionData.inspection.asset && (
                        <div className="flex flex-col gap-1">
                          <Label>Asset</Label>
                          <div className="bg-grey-1 ring-grey-3 flex min-h-[41px] flex-wrap items-center gap-1 rounded p-2 ring-2 ring-inset">
                            <Link className="text-default-blue text-sm underline" to={getWorkspaceRoute(`/assets/${inspectionData.inspection.assetId}`)}>
                              {EntityRefUtil.createAssetLabel(inspectionData.inspection.asset)}
                            </Link>
                          </div>
                        </div>
                      )}

                      {isLegacySubmission ? (
                        <LegacySubmissionRenderer
                          data={inspectionData.inspection.legacyData}
                          location={inspectionData.inspection.location?.name}
                          createdAt={inspectionData.inspection.createdAt}
                        />
                      ) : inspectionForm && inspectionData.inspection.asset ? (
                        <FormProvider {...formInstance}>
                          <form className="flex flex-col gap-2 print:pb-16">
                            <FormElementsRenderer
                              elements={inspectionForm.elements}
                              form={inspectionForm.form as FormDTO}
                              asset={inspectionData.inspection.asset}
                              readonly={!canEditRecord}
                              lockNonEditableElements={canEditRecord}
                              isEditingExistingRecord
                            />
                          </form>
                        </FormProvider>
                      ) : undefined}

                      {!!inspectionData.inspection.asset && (
                        <ReadOnlyField label="Asset Tags">
                          <TagBadges tags={inspectionData.inspection.asset.tags} />
                        </ReadOnlyField>
                      )}
                    </>
                  )
                )}
              </div>
            </div>
          )}
          {inspectionData?.inspection && isAWA && (
            <div className="fixed right-4 bottom-4">
              <Button variant="default" size="icon" className="h-[65px] w-[65px] rounded-full" onClick={() => setIsShareDrawerOpen(true)}>
                <SendIcon size={30} />
              </Button>
            </div>
          )}{' '}
        </SheetContent>
      </SheetPortal>

      {inspectionData?.inspection && isAWA && (
        <ShareDrawer open={isShareDrawerOpen} onOpenChange={setIsShareDrawerOpen} formSubmission={inspectionData.inspection} />
      )}
      <SaveChangesDialog
        open={isConfirmOpen}
        onOpenChange={onConfirmOpenChange}
        saveCallback={formInstance.handleSubmit(
          (values) =>
            saveInspectionEditAsync(values)
              .then(() => {
                formInstance.reset(values);
                settleConfirm(true);
              })
              .catch(() => settleConfirm(false)),
          handleInvalidSave,
        )}
        discardCallback={() => {
          formInstance.reset();
          settleConfirm(true);
        }}
        loading={isSaving}
        title="Save changes?"
        description="Do you want to save your changes before leaving?"
      />
    </Sheet>
  );
}
